import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight, IndianRupee, ArrowRight } from 'lucide-react';

/* Placeholder frame for items without images */
function PlaceholderFrame({ label }) {
  return (
    <div className="aspect-[4/3] bg-visyra-bg border border-white/10 flex items-center justify-center group-hover:border-gold/20 transition-colors duration-500">
      <span className="font-body text-xs text-visyra-dim tracking-wider">{label || '800 x 533'}</span>
    </div>
  );
}

/* Individual service card */
function ServiceCard({ item }) {
  return (
    <div
      data-testid={`service-item-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
      className="group bg-visyra-card/50 border border-white/5 hover:border-gold/20 transition-all duration-500 overflow-hidden"
    >
      {item.image ? (
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        </div>
      ) : (
        <PlaceholderFrame label={item.imageLabel} />
      )}
      <div className="p-5">
        <h4 className="font-heading text-lg text-visyra-text mb-2 tracking-tight">{item.title}</h4>
        <p className="font-body text-sm text-visyra-muted leading-relaxed">{item.description}</p>
      </div>
      {/* Bottom accent */}
      <div className="h-[2px] w-0 bg-gold group-hover:w-full transition-all duration-700" />
    </div>
  );
}

export default function ServicePageLayout({ data }) {
  const [activeSection, setActiveSection] = useState(data.sections[0]?.id || '');
  const [tocOpen, setTocOpen] = useState(false);

  /* Track active section via IntersectionObserver */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-120px 0px -55% 0px' }
    );

    data.sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [data.sections]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  return (
    <div data-testid={`service-page-${data.id || 'generic'}`}>
      {/* Page Hero Banner */}
      <section className="relative h-[50vh] sm:h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={data.heroImage} alt={data.title} className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-visyra-bg via-visyra-bg/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-12 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-3"
          >
            {data.category || 'Our Services'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="service-page-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-visyra-text"
          >
            {data.title}
          </motion.h1>
          {data.subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-body text-base text-visyra-muted mt-3 max-w-xl"
            >
              {data.subtitle}
            </motion.p>
          )}
        </div>
      </section>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        {/* Mobile TOC */}
        <div className="lg:hidden mb-10">
          <button
            onClick={() => setTocOpen(!tocOpen)}
            data-testid="mobile-toc-toggle"
            className="w-full flex items-center justify-between bg-visyra-card border border-white/10 px-5 py-4 transition-colors"
          >
            <span className="font-body text-sm uppercase tracking-wider text-visyra-muted">Table of Contents</span>
            <ChevronDown size={16} className={`text-gold transition-transform duration-300 ${tocOpen ? 'rotate-180' : ''}`} />
          </button>
          {tocOpen && (
            <div className="bg-visyra-card/80 border border-white/5 border-t-0">
              {data.sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  data-testid={`mobile-toc-${section.id}`}
                  className={`w-full text-left px-5 py-3 font-body text-sm border-l-2 transition-all duration-300 ${
                    activeSection === section.id
                      ? 'text-gold border-gold bg-gold-subtle'
                      : 'text-visyra-muted border-transparent hover:text-gold'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-12 lg:gap-16">
          {/* Desktop Sticky TOC Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-visyra-dim mb-5">Sections</p>
              <nav className="space-y-0.5">
                {data.sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    data-testid={`toc-${section.id}`}
                    className={`w-full text-left px-4 py-2.5 font-body text-sm border-l-2 transition-all duration-300 flex items-center gap-2 ${
                      activeSection === section.id
                        ? 'text-gold border-gold bg-gold-subtle'
                        : 'text-visyra-muted border-white/5 hover:text-gold hover:border-gold/50'
                    }`}
                  >
                    <ChevronRight size={12} className={activeSection === section.id ? 'text-gold' : 'text-visyra-dim'} />
                    {section.title}
                  </button>
                ))}
              </nav>

              {/* Pricing Enquiry CTA in sidebar */}
              <div className="mt-8 p-5 bg-visyra-card border border-gold/20 rounded-sm">
                <div className="flex items-center gap-2 mb-3">
                  <IndianRupee size={16} className="text-gold" strokeWidth={1.5} />
                  <p className="font-body text-xs uppercase tracking-widest text-gold">Pricing</p>
                </div>
                <p className="font-body text-sm text-visyra-muted leading-relaxed mb-4">
                  Get a custom quote tailored to your requirements.
                </p>
                <Link
                  to="/contact"
                  data-testid="sidebar-pricing-enquire-btn"
                  className="flex items-center justify-center gap-2 w-full bg-gold text-visyra-bg font-body text-xs font-medium uppercase tracking-widest py-3 hover:bg-gold-light transition-colors duration-300"
                >
                  Enquire Now <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {data.sections.map((section, idx) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7 }}
                className={idx > 0 ? 'mt-20' : ''}
              >
                <h2
                  data-testid={`section-heading-${section.id}`}
                  className="font-heading text-2xl sm:text-3xl text-visyra-text tracking-tight mb-2"
                >
                  {section.title}
                </h2>
                <div className="section-divider mb-8" />

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {section.items.map((item) => (
                    <ServiceCard key={item.title} item={item} />
                  ))}
                </div>
              </motion.section>
            ))}

            {/* Pricing Enquiry Banner */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              data-testid="pricing-enquiry-banner"
              className="mt-20 relative overflow-hidden bg-visyra-card border border-gold/20 p-8 sm:p-12"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-gold-subtle border border-gold/30">
                      <IndianRupee size={18} className="text-gold" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-xl sm:text-2xl text-visyra-text tracking-tight">
                      Pricing & Packages
                    </h3>
                  </div>
                  <p className="font-body text-sm sm:text-base text-visyra-muted leading-relaxed max-w-lg">
                    Every project is unique. We offer flexible pricing and custom packages tailored to your specific needs and vision. Get in touch for a personalized quote.
                  </p>
                </div>
                <Link
                  to="/contact"
                  data-testid="pricing-enquire-btn"
                  className="btn-shimmer flex-shrink-0 flex items-center gap-2 bg-gold text-visyra-bg font-body text-sm font-medium uppercase tracking-widest px-8 py-3.5 hover:bg-gold-light transition-all duration-400"
                >
                  Enquire for Pricing <ArrowRight size={14} />
                </Link>
              </div>
              {/* Decorative accent */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gold/5 -translate-y-1/2 translate-x-1/2 rounded-full" />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
